import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { PassportService } from './passport.service';
import { PassportDetailsStore } from './passport-details.store';
import { OfflineStorageService, CachedDraft } from './offline-storage.service';
import { DateTimeHelperService } from './date-time-helper.service';
import { AuthorizerSyncService } from './authorizer-sync.service';
import { OfflineHistoryService } from './offline-history.service';
import { NetworkStatusService } from './network-status.service';
import { CommentsNotificationService } from './comments-notification.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncTime: Date | null;
  failedSyncs: string[]; // passport IDs that failed to sync
}

/** Queued submission snapshot stored in the IndexedDB `outgoing` store. */
interface OutgoingSubmissionRecord {
  data: any;
  roleId: number;
  submittedByRoleId: number;
  isSchedulerViewMode?: boolean;
  authorisedByUserID?: number;
  hasPendingAuthorizer?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SyncService {
  private static readonly staleInvoicedErrorCode = 'stale_invoiced_load';

  private readonly syncStatus = signal<SyncStatus>({
    isSyncing: false,
    lastSyncTime: null,
    failedSyncs: []
  });

  public readonly status = this.syncStatus.asReadonly();
  private isSyncInProgress = false;

  /** Passport IDs to skip in offline-history sync after invoiced rejection (draft flush or outgoing submit) */
  private readonly staleInvoicedSkipHistoryIdsSet = new Set<string>();
  
  /**
   * Subject that emits when sync completes successfully
   * Components can subscribe to trigger data refresh after coming back online
   */
  private readonly syncCompleteSubject = new Subject<void>();
  public readonly syncComplete$ = this.syncCompleteSubject.asObservable();
  
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly authorizerSync = inject(AuthorizerSyncService);
  private readonly offlineHistoryService = inject(OfflineHistoryService);
  private readonly networkStatus = inject(NetworkStatusService);
  private readonly commentsNotificationService = inject(CommentsNotificationService);
  private readonly toastService = inject(ToastService);

  constructor(
    private readonly http: HttpClient,
    private readonly passportService: PassportService,
    private readonly detailsStore: PassportDetailsStore
  ) {
    this.setupOnlineListener();
  }

  /**
   * Check if offline features should be used (mobile only)
   */
  private shouldUseOfflineFeatures(): boolean {
    if (globalThis.window === undefined || globalThis.window.innerWidth >= 768) {
      return false;
    }
    const persona = (localStorage.getItem('persona') || '').toLowerCase();
    return persona.includes('grower') || persona.includes('transload');
  }

  private isStaleInvoicedHttpError(error: unknown): boolean {
    const err = error as { status?: number; error?: { error?: string } };
    return err.status === 400 && err.error?.error === SyncService.staleInvoicedErrorCode;
  }

  /**
   * Clears offline queues and refreshes server state when submit/draft is rejected because the load was invoiced.
   */
  private async handleStaleInvoicedLoadDuringSync(
    passportId: string,
    serverMessage = 'This load has been invoiced while you were offline. Local changes were not saved. Refresh the list to load the latest data.'
  ): Promise<void> {
    this.toastService.warning(serverMessage, 7000);
    try {
      await this.offlineStorage.deleteOutgoingPassport(passportId);
    } catch {
      /* ignore */
    }
    try {
      await this.offlineStorage.deleteDraft(passportId);
    } catch {
      /* ignore */
    }
    this.passportService.setStatus(passportId, 'pending', true);
    this.staleInvoicedSkipHistoryIdsSet.add(passportId);
  }

  /**
   * Set up listener for online event to automatically trigger sync
   * NEW ORDER:
   * 1. Authorizers first - sync pending authorizers to get real IDs
   * 2. Drafts second - updates non-image fields (may reference authorizers)
   * 3. Submissions third - handles image operations and final submission (may reference authorizers)
   * ONLY ACTIVE IN MOBILE MODE
   */
  private setupOnlineListener(): void {
    globalThis.addEventListener('online', () => {
      if (!this.shouldUseOfflineFeatures()) {
        console.log('Desktop mode detected - skipping auto-sync');
        return;
      }
      console.log('Network connection detected - triggering auto-sync (mobile mode)');
      // Sync in sequence: authorizers → drafts → submissions
      void this.syncInSequence();
    });
  }

  /**
   * Sync authorizers, drafts, histories, and outgoing submissions in the correct order
   * ORDER:
   * 1. Authorizers first - sync pending authorizers to get real IDs
   * 2. Drafts second - updates non-image fields (may reference authorizers)
   * 3. Outgoing submissions - full submit (images); fails early if load invoiced upstream
   * 4. Offline history last - skip passports that failed draft flush or outgoing (invoiced, etc.)
   * ONLY RUNS IN MOBILE MODE
   */
  private async syncInSequence(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Desktop mode detected - skipping sync');
      return;
    }

    try {
      // STEP 1: Sync pending authorizers first
      console.log('🔄 Step 1/4: Syncing pending authorizers...');
      const authorizersSynced = await this.authorizerSync.syncPendingAuthorizers();
      if (!authorizersSynced) {
        console.warn('⚠️ Some authorizers failed to sync, but continuing with drafts, histories and submissions');
      }
      
      // STEP 2: Sync drafts (non-image fields)
      console.log('🔄 Step 2/4: Syncing drafts (non-image fields)...');
      this.staleInvoicedSkipHistoryIdsSet.clear();
      const failedDraftIds = (await this.submitDraftsWhenOnline()) ?? [];

      // STEP 3: Outgoing submissions BEFORE offline history so invoiced rejection does not leave bogus history uploads
      console.log('🔄 Step 3/4: Syncing outgoing submissions (with images)...');
      await this.syncOutgoingRecords();

      const skipHistoryPassportIds = [...new Set([...failedDraftIds, ...this.staleInvoicedSkipHistoryIdsSet])];

      // STEP 4: Offline history — skip passports whose draft or outgoing sync failed (including invoiced)
      console.log('🔄 Step 4/4: Syncing offline history entries...');
      await this.syncOfflineHistories(skipHistoryPassportIds);
      
      console.log('✅ Sequential sync complete');
      
      // Notify subscribers that sync is complete so they can refresh data
      this.syncCompleteSubject.next();
    } catch (error) {
      console.warn('❌ Error during sequential sync:', error);
    }
  }

  /**
   * Get all outgoing (unsynced) records
   * Only returns records with status 'outgoing' - 'pending' records are legitimate API records that shouldn't be synced
   */
  private getOutgoingRecords(): Array<{ id: string; status: string; isSynced: boolean }> {
    const allRecords = this.passportService.records();
    // Only include 'outgoing' status passports that need to be synced
    // 'pending' records are legitimate API records, not offline submissions to sync
    return allRecords.filter(r => 
      r.status === 'outgoing' && !r.isSynced
    );
  }

  /**
   * Sync all outgoing records to the backend
   * ROBUST IMPLEMENTATION: Records only move from Outgoing after BOTH sync AND refresh succeed
   */
  async syncOutgoingRecords(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Desktop mode or non-eligible persona - skipping syncOutgoingRecords');
      return;
    }
    const preconditions = await this.validateSyncPreconditions();
    if (!preconditions) return;

    const { allOutgoingIds, outgoingPassportsFromDB } = preconditions;
    
    this.isSyncInProgress = true;
    this.syncStatus.update(s => ({ ...s, isSyncing: true }));

    const failedSyncs: string[] = [];
    const pendingCleanup: string[] = [];
    const syncToastId = this.toastService.showSyncing(`Syncing ${allOutgoingIds.size} records...`);
    const syncStartTime = Date.now();

    try {
      console.log(`Starting sync for ${allOutgoingIds.size} outgoing records (${outgoingPassportsFromDB.length} from IndexedDB)`);

      for (const passportId of allOutgoingIds) {
        const result = await this.processSinglePassportSync(passportId);
        if (result === 'success') {
          pendingCleanup.push(passportId);
        } else {
          failedSyncs.push(passportId);
        }
      }

      await this.performPostSyncCleanup(pendingCleanup, failedSyncs, outgoingPassportsFromDB, syncStartTime);
      this.logSyncSummary(failedSyncs, pendingCleanup);
    } catch (error) {
      console.warn('❌ Unexpected error during sync process:', error);
      this.toastService.error('Sync process error - please try again');
    } finally {
      this.finalizeSyncStatus(failedSyncs, syncToastId);
    }
  }

  /**
   * Validate preconditions for sync operation
   */
  private async validateSyncPreconditions(): Promise<{ allOutgoingIds: Set<string>; outgoingPassportsFromDB: any[] } | null> {
    if (this.isSyncInProgress) {
      console.log('Sync already in progress, skipping...');
      return null;
    }

    const isReallyOnline = await this.networkStatus.forceCheck();
    if (!navigator.onLine || !isReallyOnline) {
      console.log('Device is offline, cannot sync');
      this.toastService.warning('Device is offline - sync skipped');
      return null;
    }

    const outgoingPassportsFromDB = await this.offlineStorage.getAllOutgoingPassports();
    const outgoingRecords = this.getOutgoingRecords();
    
    const allOutgoingIds = new Set<string>();
    for (const p of outgoingPassportsFromDB) {
      allOutgoingIds.add(p.passportId);
    }
    for (const r of outgoingRecords) {
      allOutgoingIds.add(r.id);
    }
    
    if (allOutgoingIds.size === 0) {
      console.log('No outgoing records to sync');
      return null;
    }

    return { allOutgoingIds, outgoingPassportsFromDB };
  }

  /**
   * Process sync for a single passport with error handling
   */
  private async processSinglePassportSync(passportId: string): Promise<'success' | 'failed'> {
    if (!navigator.onLine) {
      console.warn(`⚠️ Offline detected before syncing ${passportId}`);
      this.toastService.warning(`Offline - skipping ${passportId}`);
      return 'failed';
    }

    try {
      await this.syncSingleRecordWithTimeout(passportId, 120000);
      console.log(`✅ API sync completed for passport ${passportId}`);
      
      const stillOnline = navigator.onLine && await this.networkStatus.forceCheck();
      if (stillOnline) {
        return 'success';
      }
      
      console.warn(`⚠️ Network dropped during/after sync of ${passportId} - treating as uncertain`);
      this.toastService.warning(`Network dropped during sync of ${passportId}`);
      return 'failed';
    } catch (error: unknown) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      if ((error as { staleInvoiced?: boolean }).staleInvoiced === true) {
        console.warn(`Passport ${passportId} sync skipped: load invoiced upstream`);
        return 'failed';
      }
      console.warn(`❌ Failed to sync passport ${passportId}:`, error);
      this.toastService.error(`Sync failed: ${passportId} - ${errorMsg}`, 5000);
      return 'failed';
    }
  }

  /**
   * Perform post-sync cleanup and finalization
   */
  private async performPostSyncCleanup(
    pendingCleanup: string[],
    failedSyncs: string[],
    outgoingPassportsFromDB: any[],
    syncStartTime: number
  ): Promise<void> {
    if (pendingCleanup.length === 0) return;

    if (!navigator.onLine) {
      console.warn('⚠️ Device went offline before cleanup - records stay in Outgoing');
      this.toastService.warning('Device offline - records kept in Outgoing for retry');
      return;
    }

    console.log(`🧹 Cleaning up cached data for ${pendingCleanup.length} synced passports`);
    await this.cleanupSyncedPassportsCache(pendingCleanup);
    
    const refreshSuccess = await this.refreshDataFromAPI(pendingCleanup);
    await this.verifyAndFinalizeSync(pendingCleanup, failedSyncs, outgoingPassportsFromDB, syncStartTime, refreshSuccess);

    // PHASE 2: Fire-and-forget background delta sync for comments, history, and notifications.
    // Spinner is already dismissed at this point -- user can interact.
    if (refreshSuccess && pendingCleanup.length > 0) {
      void this.refreshCachedDataInBackground(pendingCleanup);
    }
  }

  /**
   * Post-sync refresh gate. Passport field data is repopulated via syncComplete$
   * (loadPassportsFromAPI / cacheAllDataForOffline), not a per-passport details call.
   */
  private async refreshDataFromAPI(_pendingCleanup: string[]): Promise<boolean> {
    return true;
  }

  /**
   * Verify online status and finalize sync by deleting from IndexedDB
   */
  private async verifyAndFinalizeSync(
    pendingCleanup: string[],
    failedSyncs: string[],
    outgoingPassportsFromDB: any[],
    syncStartTime: number,
    refreshSuccess: boolean
  ): Promise<void> {
    const finalOnlineCheck = navigator.onLine && await this.networkStatus.forceCheck();
    
    if (refreshSuccess && finalOnlineCheck) {
      await this.markRecordsAsCompleted(pendingCleanup, outgoingPassportsFromDB);
      const duration = ((Date.now() - syncStartTime) / 1000).toFixed(1);
      this.toastService.success(`✓ ${pendingCleanup.length} records synced successfully (${duration}s)`, 4000);
    } else {
      console.warn('⚠️ Final verification failed - records stay in IndexedDB for retry');
      this.toastService.warning('Sync uncertain - records kept in Outgoing for retry');
      failedSyncs.push(...pendingCleanup);
      pendingCleanup.length = 0;
    }
  }

  /**
   * Mark synced records as completed and delete from IndexedDB
   */
  private async markRecordsAsCompleted(pendingCleanup: string[], outgoingPassportsFromDB: any[]): Promise<void> {
    for (const passportId of pendingCleanup) {
      const wasInDB = outgoingPassportsFromDB.some(p => p.passportId === passportId);
      if (wasInDB) {
        await this.offlineStorage.deleteOutgoingPassport(passportId);
        console.log(`🗑️ Deleted outgoing passport ${passportId} from IndexedDB`);
      }
      this.passportService.setStatus(passportId, 'completed', true);
    }
  }

  /**
   * Log sync summary
   */
  private logSyncSummary(failedSyncs: string[], pendingCleanup: string[]): void {
    if (failedSyncs.length === 0 && pendingCleanup.length > 0) {
      console.log('✅ All outgoing records synced successfully');
    } else if (failedSyncs.length > 0) {
      console.warn(`⚠️ ${failedSyncs.length} records failed/uncertain:`, failedSyncs);
    }
  }

  /**
   * Finalize sync status and dismiss toast
   */
  private finalizeSyncStatus(failedSyncs: string[], syncToastId: number): void {
    this.isSyncInProgress = false;
    this.syncStatus.update(s => ({
      isSyncing: false,
      lastSyncTime: new Date(),
      failedSyncs
    }));
    this.toastService.dismiss(syncToastId);
  }

  /**
   * Sync a single record with timeout to prevent hanging on slow networks
   * Default timeout is 120 seconds to accommodate 2G/slow connections
   */
  private async syncSingleRecordWithTimeout(passportId: string, timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Sync timeout after ${timeoutMs / 1000}s`));
      }, timeoutMs);
      
      this.syncSingleRecord(passportId)
        .then(() => { clearTimeout(timeout); resolve(); })
        .catch(err => { clearTimeout(timeout); reject(err); });
    });
  }

  /**
   * Save all drafts when device comes back online (using draft save API)
   * Can be called manually or automatically via online event listener
   */
  async submitDraftsWhenOnline(): Promise<string[]> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Desktop mode or non-eligible persona - skipping submitDraftsWhenOnline');
      return [];
    }
    if (!navigator.onLine) {
      console.log('Device is offline, cannot save drafts');
      return [];
    }

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const persona = localStorage.getItem('persona') || 'grower';
    const userID = localStorage.getItem('userID');

    if (!token || !userID) {
      console.log('No authentication token or user ID available, cannot save drafts');
      return [];
    }

    try {
      const allDrafts = await this.offlineStorage.getAllDrafts();
      
      if (allDrafts.length === 0) {
        console.log('No drafts to save');
        return [];
      }

      console.log(`📝 Found ${allDrafts.length} draft(s) to save when online`);

      const roleId = this.getRoleIdForPersona(persona);
      const failedSaves: string[] = [];

      for (const draft of allDrafts) {
        try {
          await this.saveSingleDraft(draft, roleId, tokenType, token);
        } catch (error: unknown) {
          console.warn(`❌ Failed to save draft for passport ${draft.passportId}:`, error);
          failedSaves.push(draft.passportId);
          if (this.isStaleInvoicedHttpError(error)) {
            const msg = (error as { error?: { message?: string } }).error?.message;
            await this.handleStaleInvoicedLoadDuringSync(draft.passportId, msg);
            continue;
          }
          await this.offlineStorage.deleteDraft(draft.passportId);
          console.log(`🗑️ Deleted failed draft ${draft.passportId} to ensure fresh API data`);
        }
      }

      if (failedSaves.length === 0) {
        console.log('✅ All drafts saved successfully');
      } else {
        console.warn(`⚠️ ${failedSaves.length} drafts failed to save:`, failedSaves);
      }
      
      return failedSaves;
    } catch (error) {
      console.warn('Error saving drafts:', error);
      return [];
    }
  }

  /**
   * Saves a single draft to the backend
   */
  private async saveSingleDraft(draft: CachedDraft, roleId: number, tokenType: string, token: string): Promise<void> {
    const passportId = draft.passportId;
    
    // Check if outgoing submission exists for this passport
    const hasOutgoing = await this.offlineStorage.getOutgoingPassport(passportId);
    const skipImages = !!hasOutgoing;
    
    if (hasOutgoing) {
      console.log(`📋 Syncing draft for ${passportId} (non-image fields only - images handled by outgoing submission)`);
    }
    
    const draftData = draft.data;

    // Convert Blobs back to Files if needed
    const passportData = this.offlineStorage.convertBlobsToFiles(draftData);

    // Save the draft using draft save API (PUT /api/v1/passports/update/{id}/role/{roleId})
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/update/${passportId}/role/${roleId}`;
    const resolvedAuthorisedByUserID = await this.resolveMissingAuthorizerId(passportData, passportId, roleId);
    const formData = this.buildDraftFormData(passportData, skipImages, resolvedAuthorisedByUserID);

    const headers = { 'Authorization': `${tokenType} ${token}` };
    
    console.log(`📤 Saving draft for passport ${passportId}`);
    
    const response = await firstValueFrom(
      this.http.put<any>(apiUrl, formData, { headers, withCredentials: true })
    );

    // Check for errors
    if (response?.draft?.status === 'Error' || response?.error) {
      const errorMessage = response?.draft?.message || response?.message || response?.error || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    console.log(`✅ Successfully saved draft for passport ${passportId}`);

    // Update the store with authorizer name if it exists in the draft data
    await this.updateStoreWithAuthorizer(passportId, passportData);

    // Delete draft after successful save
    await this.offlineStorage.deleteDraft(passportId);
    console.log(`🗑️ Deleted draft ${passportId} after successful save`);
  }

  /**
   * Updates the store with authorizer name if it exists in the draft data
   */
  private async updateStoreWithAuthorizer(passportId: string, passportData: any): Promise<void> {
    if (passportData?.authorisedBy?.trim()) {
      const authorizerId = this.authorizerSync.getAuthorizerId(passportData.authorisedBy.trim());
      if (authorizerId) {
        // Update the store with the authorizer name
        const currentStoreData = this.detailsStore.get(passportId);
        if (currentStoreData) {
          this.detailsStore.update(passportId, {
            authorisedBy: passportData.authorisedBy.trim()
          });
          console.log(`✅ Updated store with authorizer "${passportData.authorisedBy}" for passport ${passportId}`);
        }
      }
    }
  }

  /**
   * Sync a single passport record to the backend using FormData (same as submitToBackend)
   */
  private async syncSingleRecord(passportId: string): Promise<void> {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const persona = localStorage.getItem('persona') || 'grower';
    const userID = localStorage.getItem('userID');

    if (!token || !userID) {
      throw new Error('No authentication token or user ID available');
    }

    const { passportData, roleId, submittedByRoleId, authorisedByUserID } =
      await this.resolvePassportSyncData(passportId, persona);

    // Use backend path parameters
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/submit/${passportId}/role/${roleId}/submittedBy/${submittedByRoleId}`;
    
    // Build FormData (same structure as submitToBackend)
    const formData = this.buildSubmitFormData(passportData, passportId, authorisedByUserID);

    // Submit to backend
    const headers = { 'Authorization': `${tokenType} ${token}` };
    
    console.log(`🔄 Syncing passport ${passportId} to ${apiUrl}`);
    
    let response: any;
    try {
      response = await firstValueFrom(
        this.http.post<any>(apiUrl, formData, { headers, withCredentials: true })
      );
    } catch (httpError: unknown) {
      if (this.isStaleInvoicedHttpError(httpError)) {
        const msg = (httpError as { error?: { message?: string } }).error?.message;
        await this.handleStaleInvoicedLoadDuringSync(passportId, msg);
        const marker = new Error('stale_invoiced_load');
        (marker as { staleInvoiced?: boolean }).staleInvoiced = true;
        throw marker;
      }
      const errAny = httpError as { error?: { error?: string } };
      const errorCode = errAny?.error?.error;
      if (errorCode === 'workflow_trigger_failed' || errorCode === 'image_processing_failed') {
        throw new Error('Connection to the processing service failed. Your submission has been reverted.');
      }
      throw httpError;
    }

    if (response?.error === 'workflow_trigger_failed' || response?.error === 'image_processing_failed') {
      throw new Error('Connection to the processing service failed. Your submission has been reverted.');
    }

    console.log(`✅ Sync response for passport ${passportId}:`, response);
    console.log('📊 Backend response: Deleted=' + (response.deletedImages?.length || 0) + ', Added=' + (response.addedImages?.length || 0) + ', Total=' + (response.imageCount || 0));
    
    return response;
  }

  /**
   * Resolve passport data and role IDs from IndexedDB (preferred) or the in-memory detailsStore.
   */
  private async resolvePassportSyncData(
    passportId: string,
    persona: string
  ): Promise<{ passportData: any; roleId: number; submittedByRoleId: number; authorisedByUserID?: number }> {
    const submissionData = await this.offlineStorage.getOutgoingPassport(passportId);

    if (submissionData && typeof submissionData === 'object' && 'data' in submissionData) {
      return await this.extractFromIndexedDB(passportId, submissionData);
    }

    return await this.extractFromDetailsStore(passportId, persona);
  }

  private async extractFromIndexedDB(
    passportId: string,
    submissionData: unknown
  ): Promise<{ passportData: any; roleId: number; submittedByRoleId: number; authorisedByUserID?: number }> {
    console.log(`📦 Using submission data from IndexedDB for passport ${passportId}`);
    const data = submissionData as OutgoingSubmissionRecord;
    const passportData = data.data;
    let authorisedByUserID = data.authorisedByUserID;

    const deletedFilenames = passportData?.deletedImageFilenames || [];
    console.log(`🔄 [SYNC] Syncing passport ${passportId}:`, {
      authorisedBy: passportData?.authorisedBy,
      authorisedByUserID,
      hasPendingAuthorizer: data.hasPendingAuthorizer,
      deletedImageFilenames: deletedFilenames
    });

    if (!authorisedByUserID && passportData?.authorisedBy?.trim()) {
      const resolvedId = await this.resolveAuthorisedByUserIdForSync(passportData.authorisedBy, {
        passportId,
        roleId: data.roleId
      });

      if (resolvedId) {
        authorisedByUserID = resolvedId;
        console.log(`✅ Found authorizer ID for "${passportData.authorisedBy}": ${resolvedId}`);
        await this.persistAuthorizerIdOnOutgoing(passportId, data, resolvedId);
      } else {
        console.warn(`⚠️ authorisedBy name exists ('${passportData.authorisedBy}') but authorisedByUserID not available after in-memory, offline cache and API lookup`);
      }
    }

    return { passportData, roleId: data.roleId, submittedByRoleId: data.submittedByRoleId, authorisedByUserID };
  }

  private async extractFromDetailsStore(
    passportId: string,
    persona: string
  ): Promise<{ passportData: any; roleId: number; submittedByRoleId: number; authorisedByUserID?: number }> {
    const passportData = this.detailsStore.get(passportId);
    if (!passportData) {
      throw new Error(`No data found for passport ${passportId}`);
    }

    const roleId = this.getRoleIdForPersona(persona);
    const authorisedByUserID = await this.resolveMissingAuthorizerId(passportData, passportId, roleId);

    if (!authorisedByUserID && passportData?.authorisedBy?.trim()) {
      console.warn(`⚠️ authorisedBy name exists (${passportData.authorisedBy}) but authorisedByUserID not available in fallback sync path`);
    }

    return { passportData, roleId, submittedByRoleId: roleId, authorisedByUserID };
  }

  /**
   * Resolves an authorizer ID for a submission that only carries the authorizer name.
   * In-memory map (populated while passport-details is open) → cached authorizer list in
   * IndexedDB → authorizers add API, which returns the existing ID for a known name.
   */
  private async resolveAuthorisedByUserIdForSync(
    authorisedBy: string,
    context: { passportId: string; roleId?: number }
  ): Promise<number | undefined> {
    const trimmed = authorisedBy.trim();

    const fromMemory = this.authorizerSync.getAuthorizerId(trimmed);
    if (fromMemory) {
      return fromMemory;
    }

    return await this.authorizerSync.resolveAuthorizerIdFromCacheOrApi(trimmed, context);
  }

  /**
   * Returns a resolved ID only when the record has a name but no ID, so a record that already
   * carries an ID never triggers a lookup.
   */
  private async resolveMissingAuthorizerId(
    data: any,
    passportId: string,
    roleId: number
  ): Promise<number | undefined> {
    if (data?.authorisedByUserID) {
      return data.authorisedByUserID;
    }

    const name = data?.authorisedBy?.trim();
    if (!name) {
      return undefined;
    }

    return await this.resolveAuthorisedByUserIdForSync(name, { passportId, roleId });
  }

  /**
   * Freezes the resolved ID onto the queued submission so a later retry does not repeat the
   * lookup and still succeeds if the app goes offline again before the submit lands.
   */
  private async persistAuthorizerIdOnOutgoing(
    passportId: string,
    submissionData: OutgoingSubmissionRecord,
    authorisedByUserID: number
  ): Promise<void> {
    try {
      await this.offlineStorage.saveOutgoingPassport(passportId, {
        ...submissionData,
        authorisedByUserID,
        hasPendingAuthorizer: false
      });
      console.log(`💾 Stored resolved authorisedByUserID ${authorisedByUserID} on outgoing passport ${passportId}`);
    } catch (error) {
      console.warn(`⚠️ Could not persist resolved authorisedByUserID for passport ${passportId}:`, error);
    }
  }

  /**
   * Build FormData for passport submission (same structure as submitToBackend)
   */
  private buildSubmitFormData(data: any, passportId: string, authorisedByUserID?: number): FormData {
    const formData = new FormData();

    // Recalculate HACCP check based on current image state (prevents sending stale value).
    // Images are sent as base64 text (from haccpImages data URLs), so count those.
    const backendImageCount = data.imageCount || 0;
    const deletedCount = (data.deletedImageFilenames || []).length;
    const addedCount = this.getBase64Images(data).length;
    const calculatedImageCount = backendImageCount - deletedCount + addedCount;
    data.haccpCheck = calculatedImageCount > 0 ? 'Yes' : 'No';
    console.log(`🔢 Recalculated HACCP for sync: ${backendImageCount} - ${deletedCount} + ${addedCount} = ${calculatedImageCount} → ${data.haccpCheck}`);

    this.appendBasicFields(formData, data, authorisedByUserID);
    this.appendDateTimeFields(formData, data);
    this.appendOriginFields(formData, data);
    this.appendImageFields(formData, data);

    const deletedImageFilenames = data.deletedImageFilenames || [];
    const uploadCount = this.getBase64Images(data).length;
    console.log('📤 Built FormData for sync: Delete=' + deletedImageFilenames.length + ', Upload=' + uploadCount);
    return formData;
  }

  /**
   * Build FormData for draft save API (same structure as buildDraftFormData in passport-details.page.ts)
   * Draft API uses lowercase camelCase field names (no Request. prefix)
   * @param skipImages - If true, skip all image-related fields (used when outgoing submission exists)
   * @param resolvedAuthorisedByUserID - ID resolved during sync when the draft only has a name
   */
  private buildDraftFormData(data: any, skipImages: boolean = false, resolvedAuthorisedByUserID?: number): FormData {
    const formData = new FormData();

    // Ensure HACCP is always present to avoid no_fields_to_update on draft sync.
    const hasHaccpValue = data?.haccpCheck !== undefined && data?.haccpCheck !== null;
    if (!skipImages || !hasHaccpValue) {
      this.recalculateHaccpCheck(data);
    } else {
      console.log(`⏭️ Skipping HACCP recalculation - images handled by outgoing submission`);
    }

    // Append all field types
    this.appendDraftBasicFields(formData, data);
    this.appendDraftAuthorizedBy(formData, data, resolvedAuthorisedByUserID);
    this.appendDraftDateTimeFields(formData, data);
    this.appendDraftOriginFields(formData, data);
    
    // Handle images
    if (skipImages) {
      console.log('📤 Built FormData for draft save (non-image fields only)');
    } else {
      this.appendDraftImageFields(formData, data);
      const deletedImageFilenames = data.deletedImageFilenames || [];
      const uploadCount = this.getBase64Images(data).length;
      console.log('📤 Built FormData for draft save: Delete=' + deletedImageFilenames.length + ', Upload=' + uploadCount);
    }
    
    return formData;
  }

  private recalculateHaccpCheck(data: any): void {
    // Images are sent as base64 text (from haccpImages data URLs), so count those.
    const backendImageCount = data.imageCount || 0;
    const deletedCount = (data.deletedImageFilenames || []).length;
    const addedCount = this.getBase64Images(data).length;
    const calculatedImageCount = backendImageCount - deletedCount + addedCount;
    data.haccpCheck = calculatedImageCount > 0 ? 'Yes' : 'No';
    console.log(`🔢 Recalculated HACCP for draft: ${backendImageCount} - ${deletedCount} + ${addedCount} = ${calculatedImageCount} → ${data.haccpCheck}`);
  }

  private appendDraftBasicFields(formData: FormData, data: any): void {
    if (data?.bulkerNumber?.trim()) {
      formData.append('trailerNumber', data.bulkerNumber.trim());
    }

    if (data?.haccpCheck !== undefined && data?.haccpCheck !== null) {
      const isHaccpYes = data.haccpCheck === 'Yes' || data.haccpCheck === true;
      formData.append('haccpCheck', isHaccpYes ? 'true' : 'false');
    }
  }

  private appendDraftAuthorizedBy(formData: FormData, data: any, resolvedAuthorisedByUserID?: number): void {
    let authorisedByUserID = data?.authorisedByUserID ?? resolvedAuthorisedByUserID;
    
    // If ID is missing but name exists, try to get it from map (after authorizers have been synced)
    if (!authorisedByUserID && data?.authorisedBy?.trim()) {
      const authorizerId = this.authorizerSync.getAuthorizerId(data.authorisedBy.trim());
      if (authorizerId) {
        authorisedByUserID = authorizerId;
        console.log(`✅ Found authorizer ID for draft "${data.authorisedBy}": ${authorizerId}`);
      }
    }
    
    if (authorisedByUserID) {
      formData.append('authorisedByUserID', authorisedByUserID.toString());
      console.log(`✅ Including authorisedByUserID from draft: ${authorisedByUserID}`);
      
      // IMPORTANT: Update the store with the authorizer name immediately
      // This ensures the UI shows the correct authorizer after sync
      const passportId = data?.passportID || data?.passportId;
      if (passportId && data?.authorisedBy?.trim()) {
        const currentStoreData = this.detailsStore.get(passportId);
        if (currentStoreData) {
          this.detailsStore.update(passportId, {
            authorisedBy: data.authorisedBy.trim()
          });
          console.log(`✅ Updated store with authorizer name "${data.authorisedBy}" for passport ${passportId}`);
        }
      }
    } else if (data?.authorisedBy?.trim()) {
      console.warn('⚠️ Draft has authorisedBy name but no authorisedByUserID - skipping user ID');
    }
  }

  private appendDraftDateTimeFields(formData: FormData, data: any): void {
    if (data?.loadingDateTime) {
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.loadingDateTime);
      formData.append('loadingDateTime', utcValue);
    }
    
    if (data?.plannedDateTime) {
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.plannedDateTime);
      formData.append('plannedDateTime', utcValue);
    }
  }

  private appendDraftOriginFields(formData: FormData, data: any): void {
    if (!data?.origin?.trim()) return;

    const originValue = data.origin.trim();
    const originMappings: Record<string, { type: string; prefix: string }> = {
      'Field: ': { type: '2', prefix: 'Field: ' },
      'Storage: ': { type: '1', prefix: 'Storage: ' },
      'Plant: ': { type: '4', prefix: 'Plant: ' }
    };

    for (const [prefix, config] of Object.entries(originMappings)) {
      if (originValue.startsWith(prefix)) {
        formData.append('originName', originValue.substring(prefix.length));
        formData.append('rawSourceType', config.type);
        return;
      }
    }
    
    formData.append('originName', originValue);
  }

  /**
   * Append basic fields to FormData
   */
  private appendBasicFields(formData: FormData, data: any, authorisedByUserID?: number): void {
    if (data?.bulkerNumber?.trim()) {
      formData.append('Request.TrailerNumber', data.bulkerNumber.trim());
    }
    
    if (data?.haccpCheck !== undefined) {
      formData.append('Request.HaccpCheck', data.haccpCheck === 'Yes' ? 'true' : 'false');
    }

    // Authorised by user ID (from saved submission data)
    if (authorisedByUserID) {
      formData.append('Request.AuthorisedByUserID', authorisedByUserID.toString());
      console.log(`✅ Including authorisedByUserID in sync: ${authorisedByUserID}`);
    }
  }

  /**
   * Append date/time fields to FormData
   */
  private appendDateTimeFields(formData: FormData, data: any): void {
    if (data?.loadingDateTime) {
      // Ensure UTC timestamp has 'Z' suffix so backend parses it as UTC (not Unspecified)
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.loadingDateTime);
      formData.append('Request.LoadingDateTime', utcValue);
    }
    
    if (data?.plannedDateTime) {
      // Ensure UTC timestamp has 'Z' suffix so backend parses it as UTC (not Unspecified)
      const utcValue = this.dateTimeHelper.ensureUtcTimestamp(data.plannedDateTime);
      formData.append('Request.PlannedDateTime', utcValue);
    }
  }

  /**
   * Append origin fields to FormData
   */
  private appendOriginFields(formData: FormData, data: any): void {
    if (!data?.origin?.trim()) {
      return;
    }

    const originValue = data.origin.trim();
    if (originValue.startsWith('Field: ')) {
      formData.append('Request.OriginName', originValue.substring(7));
      formData.append('Request.RawSourceType', '2');
    } else if (originValue.startsWith('Storage: ')) {
      formData.append('Request.OriginName', originValue.substring(9));
      formData.append('Request.RawSourceType', '1');
    } else if (originValue.startsWith('Plant: ')) {
      formData.append('Request.OriginName', originValue.substring(7));
      formData.append('Request.RawSourceType', '4');
    } else {
      // Fallback: send as-is
      formData.append('Request.OriginName', originValue);
    }
  }

  /**
   * iOS 26.5.2 WebKit regression workaround: offline-queued records store new HACCP images as base64
   * data URLs in `haccpImages`. Return up to 3 of them so they can be sent as text fields instead of
   * multipart file parts (a file part causes WebKit 26.5.2 to transmit an empty body).
   */
  private getBase64Images(data: any): string[] {
    const images: string[] = data?.haccpImages || [];
    return images
      .filter((img: string) => typeof img === 'string' && img.startsWith('data:'))
      .slice(0, 3);
  }

  /**
   * Append image fields to FormData (submit sync) as base64 TEXT fields (iOS 26.5.2 workaround).
   */
  private appendImageFields(formData: FormData, data: any): void {
    // Process deleted images
    const deletedImageFilenames = data.deletedImageFilenames || [];
    console.log(`🗑️ [SYNC] Processing ${deletedImageFilenames.length} deleted image filenames:`, deletedImageFilenames);
    for (const filename of deletedImageFilenames) {
      formData.append('deleteImages', filename);
      console.log(`🗑️ [SYNC] Added deleteImages: ${filename}`);
    }

    // Upload new images as base64 text fields (no file parts)
    for (const [index, dataUrl] of this.getBase64Images(data).entries()) {
      formData.append(`Request.Image${index + 1}`, dataUrl);
    }

    // Set ImageCount
    if (data.imageCount !== undefined) {
      formData.append('Request.ImageCount', data.imageCount.toString());
    }
  }

  /**
   * Append draft image fields (draft sync) as base64 TEXT fields without the Request. prefix.
   */
  private appendDraftImageFields(formData: FormData, data: any): void {
    const deletedImageFilenames = data.deletedImageFilenames || [];
    console.log(`🗑️ [DRAFT SYNC] Processing ${deletedImageFilenames.length} deleted image filenames:`, deletedImageFilenames);
    for (const filename of deletedImageFilenames) {
      formData.append('deleteImages', filename);
      console.log(`🗑️ [DRAFT SYNC] Added deleteImages: ${filename}`);
    }

    for (const [index, dataUrl] of this.getBase64Images(data).entries()) {
      formData.append(`image${index + 1}`, dataUrl);
    }
  }

  /**
   * Get role ID based on persona
   */
  private getRoleIdForPersona(persona: string): number {
    const roleMap: Record<string, number> = {
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7
    };
    const normalized = (persona || '').toLowerCase();
    return roleMap[normalized] || 2;
  }

  /**
   * Manually trigger sync (can be called from UI)
   * Uses sequential sync: authorizers → drafts → submissions
   * ONLY RUNS IN MOBILE MODE
   */
  async manualSync(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Desktop mode detected - manual sync is only available in mobile mode');
      return;
    }
    console.log('Manual sync triggered (mobile mode)');
    await this.syncInSequence();
  }

  /**
   * Clear failed syncs list
   */
  clearFailedSyncs(): void {
    this.syncStatus.update(s => ({ ...s, failedSyncs: [] }));
  }

  /**
   * Clean up cached passport data for successfully synced passports.
   * Only deletes passport field data from IndexedDB -- preserves comments and history
   * so they can be delta-merged in the background phase.
   */
  private async cleanupSyncedPassportsCache(passportIds: string[]): Promise<void> {
    try {
      for (const passportId of passportIds) {
        const numericId = Number.parseInt(passportId, 10);
        if (Number.isNaN(numericId)) {
          console.warn(`⚠️ Skipping cleanup for invalid passport ID: ${passportId}`);
          continue;
        }

        console.log(`🧹 Cleaning cached passport data for passport ${passportId} (preserving comments/history)`);

        // Only delete passport field data -- comments and history are preserved for delta merge
        try {
          await this.offlineStorage.deletePassport(numericId);
          console.log(`  ✅ Deleted passport data from IndexedDB (comments/history preserved)`);
        } catch (error) {
          console.warn(`  ❌ Failed to delete passport data for passport ${passportId}:`, error);
        }
      }

      console.log('✅ Cached passport data cleanup complete');
    } catch (error) {
      console.warn('❌ Error during cache cleanup:', error);
    }
  }

  /**
   * PHASE 2 -- Background (non-blocking) refresh for notifications, origins, and authorised users.
   * Called after the blocking passport data refresh completes and the spinner is dismissed.
   * Fire-and-forget: errors are caught and a toast is shown asking the user to manually refresh.
   *
   * NOTE: Comments and history are NOT refreshed here. After every sync, syncComplete$
   * triggers loadPassportsFromAPI -> cacheAllDataForOffline in the component, which
   * does a full re-fetch of all comments/history. Running a delta refresh here would
   * race with clearPersonaData() and be overwritten by the full re-cache.
   */
  private async refreshCachedDataInBackground(passportIds: string[]): Promise<void> {
    console.log('🔄 [Background] Starting background refresh for notifications, origins, and authorised users');

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const userID = localStorage.getItem('userID');

    if (!token || !userID) {
      console.warn('⚠️ [Background] Cannot refresh - missing authentication');
      return;
    }

    const numericUserId = Number.parseInt(userID, 10);
    let hadError = false;

    // --- Comment Notifications delta for the current user ---
    try {
      await this.deltaRefreshCommentNotifications(numericUserId, token, tokenType);
    } catch (err) {
      console.warn('[Background] Failed to delta-refresh comment notifications:', err);
      hadError = true;
    }

    // --- Origins refresh (grouped by originGroupKey) ---
    try {
      await this.refreshOriginsInBackground(passportIds, token, tokenType);
    } catch (err) {
      console.warn('[Background] Failed to refresh origins:', err);
      hadError = true;
    }

    // --- Authorised Users refresh ---
    try {
      await this.refreshAuthorisedUsersInBackground(passportIds, token, tokenType);
    } catch (err) {
      console.warn('[Background] Failed to refresh authorised users:', err);
      hadError = true;
    }

    if (hadError) {
      this.toastService.warning('Some data could not be refreshed. Pull to refresh to get the latest data.', 5000);
    } else {
      console.log('✅ [Background] Delta refresh complete for all cached data');
    }
  }

  /**
   * Delta-refresh comments for a single passport.
   * If a cached timestamp exists, fetches only comments since that time; otherwise fetches all.
   */
  private async deltaRefreshCommentsForPassport(
    passportId: number,
    userId: number,
    token: string,
    tokenType: string
  ): Promise<void> {
    const cachedTimestamp = await this.offlineStorage.getCommentsTimestamp(passportId);

    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    const body: any = {
      PassportId: passportId,
      UserId: userId
    };

    // If we have a cached timestamp, request only newer comments
    if (cachedTimestamp) {
      body.SinceDate = new Date(cachedTimestamp).toISOString();
    }

    const response = await firstValueFrom(
      this.http.post<any[]>(apiUrl, body, { headers })
    );

    const comments = (response && Array.isArray(response)) ? response : [];

    if (cachedTimestamp && comments.length > 0) {
      // Delta merge
      await this.offlineStorage.mergeComments(passportId, comments);
    } else if (!cachedTimestamp) {
      // Full save (first time or cache cleared)
      await this.offlineStorage.saveComments(passportId, comments);
    }
    // else: cachedTimestamp exists but no new comments -- nothing to do

    // Update in-memory store with merged data
    const allComments = await this.offlineStorage.getComments(passportId);
    this.detailsStore.update(passportId.toString(), { comments: allComments });
  }

  /**
   * Delta-refresh history for a single passport.
   * If a cached timestamp exists, fetches only history since that time; otherwise fetches all.
   */
  private async deltaRefreshHistoryForPassport(
    passportId: number,
    userId: number,
    token: string,
    tokenType: string
  ): Promise<void> {
    const cachedTimestamp = await this.offlineStorage.getHistoryTimestamp(passportId);

    let apiUrl = `${environment.apiBaseUrl}/api/v1/passports/history?userId=${userId}&passportId=${passportId}`;
    if (cachedTimestamp) {
      apiUrl += `&since=${new Date(cachedTimestamp).toISOString()}`;
    }

    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    const response = await firstValueFrom(
      this.http.get<any[]>(apiUrl, { headers })
    );

    const history = (response && Array.isArray(response)) ? response : [];

    if (cachedTimestamp && history.length > 0) {
      // Delta merge
      await this.offlineStorage.mergeHistory(passportId, history);
    } else if (!cachedTimestamp) {
      // Full save (first time or cache cleared)
      await this.offlineStorage.saveHistory(passportId, history);
    }

    // Update in-memory store with merged data
    const allHistory = await this.offlineStorage.getHistory(passportId);
    this.detailsStore.update(passportId.toString(), { history: allHistory });
  }

  /**
   * Delta-refresh comment notifications for the current user.
   * If a cached timestamp exists, fetches only notifications since that time;
   * otherwise fetches with FromDate = 24h ago (current default behavior).
   */
  private async deltaRefreshCommentNotifications(
    userId: number,
    token: string,
    tokenType: string
  ): Promise<void> {
    const cachedTimestamp = await this.offlineStorage.getCommentNotificationsTimestamp(userId);

    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    let fromDate: string;
    if (cachedTimestamp) {
      fromDate = new Date(cachedTimestamp).toISOString();
    } else {
      // Default: 24h ago
      const d = new Date();
      d.setHours(d.getHours() - 24);
      fromDate = d.toISOString();
    }

    const body = {
      UserId: userId,
      FromDate: fromDate
    };

    const response = await firstValueFrom(
      this.http.post<any[]>(apiUrl, body, { headers })
    );

    const notifications = (response && Array.isArray(response)) ? response : [];

    if (cachedTimestamp && notifications.length > 0) {
      await this.offlineStorage.mergeCommentNotifications(userId, notifications);
    } else {
      await this.offlineStorage.saveCommentNotifications(userId, notifications);
    }

    // Update the CommentsNotificationService signals
    await this.commentsNotificationService.refresh();
  }

  /**
   * Background refresh of origins for synced passports.
   * Only runs for the Grower persona -- Transloader displays origin read-only from passport data.
   * Groups passports by originGroupKey and fetches once per unique group.
   */
  private async refreshOriginsInBackground(passportIds: string[], token: string, tokenType: string): Promise<void> {
    const persona = (localStorage.getItem('persona') || '').toLowerCase();

    // Only grower persona needs origins refresh (transloader origin is read-only from passport data)
    if (!persona.includes('grower')) {
      console.log('[Background] Skipping origins refresh for non-grower persona');
      return;
    }

    const roleId = 2; // Grower roleId

    // Collect unique group keys with a representative passport ID for each
    const groupMap = new Map<string, number>();
    for (const pid of passportIds) {
      const details = this.detailsStore.get(pid);
      const groupKey = details?.originGroupKey;
      if (groupKey && !groupMap.has(groupKey)) {
        groupMap.set(groupKey, Number.parseInt(pid, 10));
      }
    }

    if (groupMap.size === 0) {
      console.log('[Background] No origin group keys found, skipping origins refresh');
      return;
    }

    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    console.log(`🔄 [Background] Refreshing origins for ${groupMap.size} unique group(s)`);
    for (const [groupKey, representativeId] of groupMap) {
      try {
        const apiUrl = `${environment.apiBaseUrl}/api/v1/origins?roleId=${roleId}&passportId=${representativeId}`;
        const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));

        if (response?.origins && Array.isArray(response.origins)) {
          await this.offlineStorage.saveOrigins(roleId, groupKey, response.origins);
          console.log(`[Background] Cached ${response.origins.length} origins for group "${groupKey}"`);
        }
      } catch (error: any) {
        if (error?.status !== 404) {
          console.warn(`[Background] Failed to refresh origins for group "${groupKey}":`, error);
        }
      }
    }
  }

  /**
   * Background refresh of authorised users after sync.
   * Uses the persona to determine the correct API call shape.
   */
  private async refreshAuthorisedUsersInBackground(passportIds: string[], token: string, tokenType: string): Promise<void> {
    const userID = localStorage.getItem('userID');
    const persona = (localStorage.getItem('persona') || '').toLowerCase();

    if (!userID) {
      console.warn('[Background] No userID, skipping authorised users refresh');
      return;
    }

    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };

    try {
      let apiUrl: string;
      if (persona.includes('grower')) {
        apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?userId=${userID}`;
      } else if (persona.includes('transload') && passportIds.length > 0) {
        // Transloader uses a passport ID to determine the transloading station
        const firstPassportId = passportIds[0];
        apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?passportId=${firstPassportId}`;
      } else {
        console.log('[Background] Persona does not require authorised users caching');
        return;
      }

      console.log('🔄 [Background] Refreshing authorised users');
      const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));

      if (response?.authorisedUsers) {
        // Deduplicate by authorizerId
        const seen = new Map<number, any>();
        for (const user of response.authorisedUsers) {
          const key = user.authorizerId ?? user.id;
          if (key != null && !seen.has(key)) {
            seen.set(key, user);
          }
        }
        const uniqueUsers = Array.from(seen.values());
        await this.offlineStorage.saveAuthorisedUsers(Number.parseInt(userID, 10), uniqueUsers);
        console.log(`[Background] Cached ${uniqueUsers.length} authorised users`);
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        console.warn('[Background] Failed to refresh authorised users:', error);
      }
    }
  }

  /**
   * Sync offline history entries to the backend
   * Called when coming back online to upload history entries created while offline
   */
  async syncOfflineHistories(skipPassportIds: string[] = []): Promise<boolean> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Desktop mode or non-eligible persona - skipping syncOfflineHistories');
      return false;
    }
    if (!navigator.onLine) {
      console.log('📴 Device is offline, cannot sync offline histories');
      return false;
    }

    try {
      let pendingHistories = await this.offlineHistoryService.getAllPendingOfflineHistories();
      
      // Skip history sync for passports whose draft sync failed
      if (skipPassportIds.length > 0) {
        const skipSet = new Set(skipPassportIds);
        const originalCount = pendingHistories.length;
        pendingHistories = pendingHistories.filter((h: any) => !skipSet.has(String(h.passportID)));
        const skippedCount = originalCount - pendingHistories.length;
        if (skippedCount > 0) {
          console.log(`⏭️ Skipping history sync for ${skippedCount} passports (draft sync failed)`);
        }
      }
      
      if (pendingHistories.length === 0) {
        console.log('📝 No offline history entries to sync');
        return true;
      }

      console.log(`📤 Syncing ${pendingHistories.length} offline history entries to backend...`);

      const response = await this.callSyncHistoriesApi(pendingHistories);
      console.log(`📝 Backend sync response:`, response);

      const syncedOfflineIds = this.extractSyncedHistoryIds(response, pendingHistories);

      if (syncedOfflineIds.length > 0) {
        await this.cleanupAfterHistorySync(syncedOfflineIds, pendingHistories);
      }

      return response.status === 'success' || response.status === 'Success';
    } catch (error) {
      console.warn('❌ Error syncing offline histories:', error);
      return false;
    }
  }

  private async callSyncHistoriesApi(pendingHistories: any[]): Promise<{
    status: string;
    message: string;
    insertedCount: number;
    results: Array<{
      passportID: number;
      success: boolean;
      historyID: number | null;
      action: string | null;
      errorCode: string | null;
      errorMessage: string | null;
    }>;
  }> {
    const entriesForBackend = pendingHistories.map(entry => 
      this.offlineHistoryService.convertToBackendFormat(entry)
    );

    return firstValueFrom(
      this.http.post<{
        status: string;
        message: string;
        insertedCount: number;
        results: Array<{
          passportID: number;
          success: boolean;
          historyID: number | null;
          action: string | null;
          errorCode: string | null;
          errorMessage: string | null;
        }>;
      }>(`${environment.apiBaseUrl}/api/v1/passports/sync-offline-histories`, {
        entries: entriesForBackend
      })
    );
  }

  private extractSyncedHistoryIds(response: { results: Array<{ success: boolean; passportID: number; errorMessage: string | null }> }, pendingHistories: any[]): number[] {
    const syncedOfflineIds: number[] = [];
    
    for (let i = 0; i < response.results.length; i++) {
      const result = response.results[i];
      if (result.success && pendingHistories[i]) {
        syncedOfflineIds.push(pendingHistories[i].historyID);
      } else if (!result.success) {
        console.warn(`⚠️ Failed to sync history for passport ${result.passportID}: ${result.errorMessage}`);
      }
    }
    
    return syncedOfflineIds;
  }

  private async cleanupAfterHistorySync(syncedOfflineIds: number[], _pendingHistories: any[]): Promise<void> {
    await this.offlineHistoryService.removeSyncedHistoryEntries(syncedOfflineIds);
    console.log(`✅ Synced ${syncedOfflineIds.length} offline history entries and cleaned up IndexedDB`);
    // Note: Drafts are NOT deleted here - they are synced separately via submitDraftsWhenOnline()
  }
}

